import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AlertDialog from 'components/AlertDialog';
import DropdownSelect from 'components/DropdownSelect';
import logixPanelInstance from 'i18n';
import {
  MLAApplicationComment,
  MLAApplicationRowData,
  MLAApplicationStatus
} from '../../hooks/useGetMLAApplicationList';
import { Style } from './style';

export type ReviewModalSaveData = {
  row: MLAApplicationRowData;
  status: MLAApplicationStatus.Approve | MLAApplicationStatus.Reject;
  comment: MLAApplicationComment;
};

type ReviewModalProps = {
  isOpen: boolean;
  rowData: MLAApplicationRowData | null;
  canEdit: boolean;
  isFormSubmitting: boolean;
  onClose: () => void;
  onSave: (data: ReviewModalSaveData) => void;
};

const ReviewModal = (props: ReviewModalProps): JSX.Element | null => {
  const {
    isOpen, rowData, canEdit, isFormSubmitting, onClose, onSave
  } = props;

  const { t } = useTranslation('mla', {
    i18n: logixPanelInstance
  });

  const [reviewResult, setReviewResult] = useState<
    MLAApplicationStatus.Approve | MLAApplicationStatus.Reject
  >(MLAApplicationStatus.Approve);
  const [comment, setComment] = useState<MLAApplicationComment | null>(null);

  const isRejectSelected = reviewResult === MLAApplicationStatus.Reject;

  const rejectCommentOptions = [
    {
      value: null,
      name: t('rejectCommentPlaceholder')
    },
    {
      value: MLAApplicationComment.NotQualified,
      name: t('notQualified')
    },
    {
      value: MLAApplicationComment.Duplicated,
      name: t('duplicated')
    }
  ];

  useEffect(() => {
    if (!rowData || !isOpen) return;

    setReviewResult(
      rowData.status === MLAApplicationStatus.Reject ?
        MLAApplicationStatus.Reject :
        MLAApplicationStatus.Approve
    );
    setComment(
      rowData.status === MLAApplicationStatus.Reject &&
        rowData.comment !== MLAApplicationComment.None ?
        rowData.comment :
        null
    );
  }, [rowData, isOpen]);

  if (!rowData) return null;

  const isSaveDisabled = !canEdit || (isRejectSelected && comment === null) || isFormSubmitting;

  const handleClose = (): void => {
    if (isFormSubmitting) return;

    onClose();
  };

  const handleSave = (): void => {
    if (isSaveDisabled) return;

    onSave({
      row: rowData,
      status: reviewResult,
      comment: isRejectSelected ?
        (comment as MLAApplicationComment) :
        MLAApplicationComment.None
    });
  };

  return (
    <AlertDialog
      isOpen={isOpen}
      handleClose={handleClose}
      title={t('reviewModalTitle')}
      contentSlot={(
        <Style.ReviewModalForm>
          <Style.FormRow>
            <Style.FormLabel>{t('traderName')}</Style.FormLabel>
            <Style.FormValue>{rowData.name}</Style.FormValue>
          </Style.FormRow>
          <Style.FormRow>
            <Style.FormLabel>{t('traderEmail')}</Style.FormLabel>
            <Style.FormValue>{rowData.email}</Style.FormValue>
          </Style.FormRow>
          <Style.FormRow>
            <Style.FormLabel>{t('traderNumber')}</Style.FormLabel>
            <Style.FormValue>{rowData.traderId}</Style.FormValue>
          </Style.FormRow>
          <Style.FormRow>
            <Style.FormLabel>{t('reviewResult')}</Style.FormLabel>
            <Style.FormControl>
              <Style.RadioGroup>
                <Style.RadioOption>
                  <Style.RadioInput
                    type="radio"
                    name="reviewResult"
                    checked={reviewResult === MLAApplicationStatus.Approve}
                    onChange={() => {
                      setReviewResult(MLAApplicationStatus.Approve);
                      setComment(null);
                    }}
                  />
                  {t('approved')}
                </Style.RadioOption>
                <Style.RadioOption>
                  <Style.RadioInput
                    type="radio"
                    name="reviewResult"
                    checked={reviewResult === MLAApplicationStatus.Reject}
                    onChange={() => setReviewResult(MLAApplicationStatus.Reject)}
                  />
                  {t('reject')}
                </Style.RadioOption>
              </Style.RadioGroup>
            </Style.FormControl>
          </Style.FormRow>
          <Style.FormRow $isDisabled={!isRejectSelected}>
            <Style.FormLabel>{t('rejectComment')}</Style.FormLabel>
            <Style.FormControl>
              <Style.RejectCommentDropdown $isDisabled={!isRejectSelected}>
                <DropdownSelect
                  selectedValue={comment}
                  options={rejectCommentOptions}
                  onSelect={(value) => setComment(value as MLAApplicationComment)}
                  isDisabled={!isRejectSelected}
                  width="360px"
                />
              </Style.RejectCommentDropdown>
            </Style.FormControl>
          </Style.FormRow>
        </Style.ReviewModalForm>
      )}
      actionSlot={(
        <Style.ReviewModalActions>
          <Style.ReviewModalCancelButton
            type="button"
            disabled={isFormSubmitting}
            onClick={handleClose}
          >
            {t('cancel')}
          </Style.ReviewModalCancelButton>
          <Style.ReviewModalSaveButton
            type="button"
            disabled={isSaveDisabled}
            onClick={handleSave}
          >
            {t('save')}
          </Style.ReviewModalSaveButton>
        </Style.ReviewModalActions>
      )}
    />
  );
};

export default ReviewModal;
